import _jsx from "@babel/runtime/helpers/jsx";
import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect, memo } from 'react';
import clsx from 'clsx';
import Columnizer from '../Columnizer/index.js';
import RadioWithVisual from '../RadioWithVisual/index.js';

/**
 *  RadioGroup is a stateful component representing a group of radio buttons
 *  with various customization options available.
 */
const RadioGroup = /*#__PURE__*/memo(_ref => {
  let {
    columnClasses,
    containerClasses,
    columns,
    disabled = false,
    labelClassName,
    name,
    onChange = () => {},
    options = [],
    type = 'stacked',
    value = null,
    uiContext,
    ...rest
  } = _ref;
  const isStacked = type === 'stacked';
  const checkedInitialState = value?.toString() || options[0]?.value?.toString();
  const [checked, setChecked] = useState(checkedInitialState);
  useEffect(() => {
    setChecked(checkedInitialState);
  }, [checkedInitialState, value]);
  const handleChange = useCallback(event => {
    setChecked(event.target.value);
    onChange(event);
  }, [onChange]);
  const handleInputFocus = useCallback(event => event.target.closest('label').classList.add('outline-focus'), []);
  const handleInputBlur = useCallback(event => event.target.closest('label').classList.remove('outline-focus'), []);
  const radioButtons = options.map(function (option, index) {
    if (option === void 0) {
      option = {};
    }
    const labelClassNames = clsx(labelClassName, {
      radio: true,
      'radio-inline': !isStacked && !columnClasses,
      active: labelClassName?.includes('btn') && checked
    });
    const radioInputLabelClassNames = clsx('radio-input-label', {
      'radio-selected': option.value.toString() === checked,
      'radio-unselected': option.value.toString() !== checked
    });
    if (uiContext === 'landscape') {
      return /*#__PURE__*/_jsx(RadioWithVisual, {
        option: option,
        checked: option.value.toString() === checked,
        onChange: handleChange,
        uiContext: uiContext
      }, option.label);
    }

    // clean the option prop for UI display purposes
    const {
      label,
      optionDisabled,
      secondaryLabel,
      optionClassNames,
      ...optionRest
    } = option;
    delete optionRest.name;
    return /*#__PURE__*/_jsx("label", {
      "data-cmp": "radio",
      className: clsx(labelClassNames, optionClassNames),
      htmlFor: `${name}_${optionRest.value}`
    }, `${name}_${optionRest.value}`, /*#__PURE__*/React.createElement("input", Object.assign({
      id: `${name}_${optionRest.value}`,
      name: name,
      disabled: optionDisabled || disabled,
      key: optionRest.value,
      type: "radio",
      onChange: handleChange,
      checked: option.value.toString() === checked,
      onFocus: handleInputFocus,
      onBlur: handleInputBlur
    }, optionRest)), label && /*#__PURE__*/_jsx("div", {
      className: radioInputLabelClassNames
    }, void 0, label), secondaryLabel && /*#__PURE__*/_jsx("div", {
      className: "input-secondary-label"
    }, void 0, secondaryLabel));
  });
  const columnize = /*#__PURE__*/_jsx(Columnizer, {
    items: radioButtons,
    columns: columns,
    columnClasses: columnClasses
  });
  return /*#__PURE__*/React.createElement("div", Object.assign({
    "data-cmp": "radioGroup",
    className: clsx('radio-group', containerClasses)
  }, rest), columns ? columnize : radioButtons);
});
export default RadioGroup;